import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu with hover-activated submenus.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    
    // Add simple hover listeners to menu items
    this.querySelectorAll('.menu-list__list-item').forEach(item => {
      item.addEventListener('mouseenter', this.#handleMouseEnter);
      item.addEventListener('mouseleave', this.#handleMouseLeave);
    });
    
    this.overflowMenu?.addEventListener('mouseleave', this.#handleOverflowLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    
    this.querySelectorAll('.menu-list__list-item').forEach(item => {
      item.removeEventListener('mouseenter', this.#handleMouseEnter);
      item.removeEventListener('mouseleave', this.#handleMouseLeave);
    });
    
    this.overflowMenu?.removeEventListener('mouseleave', this.#handleOverflowLeave);
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  /**
   * Handle mouse enter on a menu item
   * @param {MouseEvent} event
   */
  #handleMouseEnter = (event) => {
    clearTimeout(this.#closeTimer);
    
    const listItem = event.currentTarget;
    if (!(listItem instanceof HTMLElement)) return;
    
    const menuLink = listItem.querySelector('[ref="menuitem"]');
    if (!menuLink) return;
    
    // Don't reactivate if already active
    if (this.#state.activeItem === menuLink) return;
    
    this.#activate(listItem, menuLink);
  };

  /**
   * Handle mouse leave on a menu item
   * @param {MouseEvent} event
   */
  #handleMouseLeave = (event) => {
    const listItem = event.currentTarget;
    if (!(listItem instanceof HTMLElement)) return;
    
    const relatedTarget = event.relatedTarget;
    
    // Don't close if moving to submenu
    const submenu = listItem.querySelector('[ref="submenu[]"]');
    if (submenu && relatedTarget instanceof Node && submenu.contains(relatedTarget)) {
      return;
    }
    
    // Don't close if moving to overflow menu
    if (relatedTarget instanceof Node && this.overflowMenu?.contains(relatedTarget)) {
      return;
    }
    
    // Small delay to prevent flicker when moving between items
    this.#closeTimer = setTimeout(() => {
      this.#deactivate();
    }, 100);
  };

  /**
   * Handle mouse leave on overflow menu
   */
  #handleOverflowLeave = () => {
    this.#closeTimer = setTimeout(() => {
      this.#deactivate();
    }, 100);
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate a menu item
   * @param {HTMLElement} listItem
   * @param {HTMLElement} menuLink
   */
  #activate(listItem, menuLink) {
    this.dispatchEvent(new MegaMenuHoverEvent());

    // Deactivate previous item
    if (this.#state.activeItem && this.#state.activeItem !== menuLink) {
      this.#state.activeItem.ariaExpanded = 'false';
    }

    // Activate new item
    this.#state.activeItem = menuLink;
    this.ariaExpanded = 'true';
    menuLink.ariaExpanded = 'true';

    const isOverflowItem = listItem.hasAttribute('slot') && listItem.getAttribute('slot') !== '';
    this.dataset.overflowExpanded = isOverflowItem.toString();

    // Find and measure submenu
    const submenu = listItem.querySelector('[ref="submenu[]"]');
    let submenuHeight = 0;

    if (submenu instanceof HTMLElement) {
      submenu.dataset.active = '';
      submenuHeight = submenu.offsetHeight;
    } else if (isOverflowItem && this.overflowMenu) {
      // For overflow items without submenu, show overflow menu
      submenuHeight = this.#getOverflowListLinksHeight();
    }

    // Set heights
    if (this.headerComponent) {
      this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
      this.#setFullOpenHeaderHeight(submenuHeight);
    }
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active menu item
   */
  #deactivate() {
    if (!this.#state.activeItem) return;

    const listItem = this.#state.activeItem.closest('.menu-list__list-item');
    const submenu = listItem?.querySelector('[ref="submenu[]"]');

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem.ariaExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';

    if (submenu instanceof HTMLElement) {
      delete submenu.dataset.active;
    }
  }

  /**
   * Legacy activate method for compatibility with on:focus attributes
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;
    
    const listItem = event.target.closest('.menu-list__list-item');
    const menuLink = findMenuItem(event.target);
    
    if (listItem instanceof HTMLElement && menuLink) {
      this.#activate(listItem, menuLink);
    }
  };

  /**
   * Legacy deactivate method for compatibility with on:blur attributes
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      return;
    }

    this.#deactivate();
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('nj-header-menu')) {
  customElements.define('nj-header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
